import type { UseQueryResult } from 'react-query';

import { QueryKeys } from '@frontend/features/common/types';

export const ownershipCatalogQueryPrefix = (agencyId: number, vertical: string) =>
  [QueryKeys.getRuteroCatalog, 'ownership', agencyId, vertical] as const;

export const ownershipCatalogQueryKey = (
  agencyId: number,
  vertical: string,
  resource: string,
  ...queryParts: readonly unknown[]
) => [...ownershipCatalogQueryPrefix(agencyId, vertical), resource, ...queryParts] as const;

export const ownershipCatalogQueryOptions = (
  agencyId: number,
  vertical: string,
  resource: string,
  ...queryParts: readonly unknown[]
) => ({
  queryKey: ownershipCatalogQueryKey(agencyId, vertical, resource, ...queryParts),
  refetchOnWindowFocus: 'always' as const,
  refetchOnMount: 'always' as const,
});

export const ownershipCatalogAuthorityIsActionable = ({
  isSuccess,
  isFetching,
}: {
  isSuccess: boolean;
  isFetching: boolean;
}) => isSuccess && !isFetching;

export type OwnershipCatalogQueryResult<TData, TError = unknown> = Omit<UseQueryResult<TData, TError>, 'data'> & {
  data: TData | undefined;
  catalogAuthorityActionable: boolean;
};

/**
 * React Query conserva `data` durante un refetch y después de que ese refetch
 * falle. Para catálogos de autoridad, esas filas previas no pueden seguir
 * alimentando una selección nueva.
 */
export function withOwnershipCatalogAuthority<TData, TError = unknown>(
  query: UseQueryResult<TData, TError>,
): OwnershipCatalogQueryResult<TData, TError> {
  const catalogAuthorityActionable = ownershipCatalogAuthorityIsActionable(query);

  return {
    ...query,
    data: catalogAuthorityActionable ? query.data : undefined,
    catalogAuthorityActionable,
  } as OwnershipCatalogQueryResult<TData, TError>;
}
